import { isoDateTime, type ISODateTime } from '@bigint-project/core';
import type { ProjectTemplate } from '@desktop/persistence/project-template';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type RefObject,
} from 'react';

export interface TemplateCreateInput {
  readonly projectName: string;
  readonly anchorDate?: ISODateTime;
}

interface TemplateCreateDialogProps {
  readonly template: ProjectTemplate;
  readonly submitting: boolean;
  /** 關閉後焦點要還原到的觸發元素；檔案 input 是隱藏的，不能當還原目標。 */
  readonly returnFocusRef: RefObject<HTMLElement>;
  readonly onCancel: () => void;
  readonly onCreate: (input: TemplateCreateInput) => void;
}

/** 範本的排程錨點；`scheduleFrom` 決定平移要對齊開始日還是完成日。 */
function anchorOf(template: ProjectTemplate): ISODateTime {
  return template.project.scheduleFrom === 'ProjectStart' ? template.project.startDate : template.project.finishDate;
}

/** 只換掉日期部分，保留範本原本的上班時刻，避免平移順便改動一天內的起訖時間。 */
function replaceDate(value: ISODateTime, date: string): ISODateTime {
  return isoDateTime(`${date}${(value as string).slice(10)}`);
}

function today(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${String(now.getFullYear()).padStart(4, '0')}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function TemplateCreateDialog({ template, submitting, returnFocusRef, onCancel, onCreate }: TemplateCreateDialogProps): ReactElement {
  const anchor = anchorOf(template);
  const canRebase = template.captureMode === 'Planning';
  const anchorLabel = template.project.scheduleFrom === 'ProjectStart' ? '新專案開始日' : '新專案完成日';
  const [projectName, setProjectName] = useState(template.metadata.name);
  const [anchorInput, setAnchorInput] = useState(today());
  const [errors, setErrors] = useState<Partial<Record<'projectName' | 'anchorDate', string>>>({});
  const titleId = useId();
  const nameId = useId();
  const anchorId = useId();
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameInputRef.current?.focus();
    const trigger = returnFocusRef.current;
    return () => trigger?.focus();
  }, [returnFocusRef]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    onCancel();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const nextErrors: Partial<Record<'projectName' | 'anchorDate', string>> = {};
    if (projectName.trim().length === 0) nextErrors.projectName = '請輸入專案名稱。';
    if (canRebase && anchorInput.length === 0) nextErrors.anchorDate = `請選擇${anchorLabel}。`;
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    onCreate({
      projectName: projectName.trim(),
      ...(canRebase ? { anchorDate: replaceDate(anchor, anchorInput) } : {}),
    });
  };

  return (
    <div className="modal-backdrop" role="presentation" onKeyDown={handleKeyDown}>
      <form className="modal-card" role="dialog" aria-modal="true" aria-labelledby={titleId} onSubmit={handleSubmit}>
        <h2 id={titleId}>從範本建立專案</h2>
        <p className="muted">
          範本「{template.metadata.name}」·{canRebase ? '規劃範本' : '快照範本'}·任務 {template.project.tasks.length}、資源 {template.project.resources.length}
        </p>

        <div>
          <label htmlFor={nameId}>專案名稱</label>
          <input
            id={nameId}
            ref={nameInputRef}
            value={projectName}
            onChange={(event) => setProjectName(event.target.value)}
            maxLength={160}
            aria-invalid={errors.projectName !== undefined}
            disabled={submitting}
          />
          {errors.projectName !== undefined && <span className="field-error">{errors.projectName}</span>}
        </div>

        {canRebase ? (
          <div>
            <label htmlFor={anchorId}>{anchorLabel}</label>
            <input
              id={anchorId}
              type="date"
              value={anchorInput}
              onChange={(event) => setAnchorInput(event.target.value)}
              aria-invalid={errors.anchorDate !== undefined}
              disabled={submitting}
            />
            {errors.anchorDate === undefined
              ? <span className="muted">所有日期會依這個錨點整批平移，再依工作日曆重新排程。</span>
              : <span className="field-error">{errors.anchorDate}</span>}
          </div>
        ) : (
          <p className="muted">快照範本會原樣保留 {(anchor as string).slice(0, 10)} 起算的既有日期，不可平移。</p>
        )}

        <div className="card-actions">
          <button type="button" className="secondary" onClick={onCancel} disabled={submitting}>取消</button>
          <button type="submit" disabled={submitting}>建立專案</button>
        </div>
      </form>
    </div>
  );
}
